/** @todo Fix import cycle */
/* eslint-disable import/no-cycle */

import * as Sentry from '@sentry/react'
import { action, reaction } from 'mobx'
import { createContext } from 'react'

import { refreshAccessToken as refreshAccessTokenService } from '../services/user.service'
import { reset as analyticsReset } from '../utils/analytics'

import { announcements as announcementsStore } from './AnnouncementStore'
import { billingFilter } from './BillingFilterStore'
import { dashboard } from './DashboardStore'
import { dealerFilter } from './DealerFilterStore'
import { display } from './DisplayStore'
import { globalSettings } from './GlobalSettingsStore'
import { notifications } from './NotificationStore'
import { task } from './TaskStore'
import { user } from './UserStore'

// Analytics

// Module-scoped so it's never serialized by mobx-sync AsyncTrunk. Dedupes
// parallel triggerRefreshToken callers to avoid race-rotating the refresh token.
let refreshPromise = null

export class RootStore {
  user = user

  billingFilter = billingFilter

  dashboard = dashboard

  dealerFilter = dealerFilter

  display = display

  notifications = notifications

  task = task

  constructor() {
    // NOTE: globalSettings is intentionally NOT a property on RootStore. Child
    // stores here are serialized by mobx-sync AsyncTrunk; persisting the
    // feature flags would rehydrate stale values (with loaded: true) on refresh
    // and briefly enable gated UI. We reference the module singleton directly.
    //
    // Load the per-user feature flags once the user is authenticated AND loaded
    // (the token is rehydrated async on refresh, so user.id is the reliable
    // "ready" signal); clear them on logout.
    reaction(
      () => this.user.isAuthenticated && Boolean(this.user.user?.id),
      (ready) => {
        if (ready) {
          globalSettings.load()
          announcementsStore.load()
        } else {
          globalSettings.reset()
          announcementsStore.reset()
        }
      },
      { fireImmediately: true },
    )
  }

  /**
   * WARP-567 — drop everything scoped to whoever was signed in, keeping the tokens and
   * user that an identity swap has just applied.
   *
   * A reload alone is not enough: AsyncTrunk persists the whole RootStore, so these
   * children are written to localStorage and rehydrated on the next boot. Without this
   * the impersonated session would inherit the previous identity's dashboard, dealer and
   * billing filters, and its notification list.
   */
  resetIdentityScopedStores = action(() => {
    this.billingFilter.reset()
    this.dashboard.reset()
    this.dealerFilter.reset()
    this.display.reset()
    this.notifications.reset()
    this.task.reset()
    globalSettings.reset()
    announcementsStore.reset()
  })

  clearStore = action(() => {
    this.billingFilter.reset()
    this.dashboard.reset()
    this.dealerFilter.reset()
    this.display.reset()
    this.notifications.reset()
    this.task.reset()
    this.user.reset()
    globalSettings.reset()
    announcementsStore.reset()

    Sentry.setUser(null)
    analyticsReset()
  })

  triggerRefreshToken = action(async (failedRequest) => {
    if (!refreshPromise) {
      refreshPromise = refreshAccessTokenService(this.user.refreshToken)
        .then(({ access, refresh }) => {
          this.user.accessToken = access
          this.user.refreshToken = refresh
          return access
        })
        .catch((err) => {
          this.clearStore()
          // The response interceptor already surfaced the session-expired toast
          // for the /token/refresh/ 401. Flag this so downstream callers skip
          // their own generic error toast and we don't double-notify.
          // eslint-disable-next-line no-param-reassign
          err.sessionExpired = true
          throw err
        })
        .finally(() => {
          refreshPromise = null
        })
    }

    const access = await refreshPromise
    if (failedRequest)
      // eslint-disable-next-line no-param-reassign
      failedRequest.response.config.headers.Authorization = `Bearer ${access}`
  })
}

export const store = new RootStore()
export const RootStoreContext = createContext(store)
